'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2RepoFilePeek, PrV2RepoFileResult } from '@/lib/pr-v2/code-changes/types';
import type { FileOptions, SelectedLineRange } from '@pierre/diffs';
import { File as CodeFile } from '@pierre/diffs/react';
import { X } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { isPrV2HotkeyTextEntryTarget } from './hooks/use-pr-v2-hotkey-interactive-target';
import { PIERRE_DIFF_PALETTE_OVERRIDES } from '@/components/shared/diff/pierre-diff-palette';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { usePrV2RepoFile } from '@/data/v2/pr/use-pr-v2-repo-file';
import { createPierreFileContents } from '@/lib/pr-v2/code-changes/pierre-file-contents';
import { getPrV2ActiveFileStoreKey } from '@/lib/stores/navigation/pr-v2-active-file-store';
import { usePrV2RepoFileStore } from '@/lib/stores/pr-v2-repo-file-store';

/**
 * The diff's display settings, so a file opened from the diff is highlighted and wrapped the same
 * way. Narrowed to the presentational fields on purpose: the panel is read-only, so it has no
 * place to put a line-click, selection or gutter callback.
 */
export type PrV2RepoFileDisplayOptions = Pick<FileOptions<undefined, undefined>, 'theme' | 'themeType' | 'overflow' | 'disableLineNumbers'>;
type PrV2RepoFilePanelProps = {
  owner: string;
  repo: string;
  number: string;
  headSha: string | null;
  baseSha: string | null;
  display: PrV2RepoFileDisplayOptions;
};
const FILE_SIZE_FORMAT = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 1
});

/** A line waiting for Pierre to render its row, and the container Pierre renders rows into. */
type PendingScroll = {
  container: HTMLElement | null;
  line: number | null;
};

/**
 * The repository file opened from this pull request's diff, read-only, as a peek over the right
 * edge of the page. A column beside the diff would narrow every file on the page, and chapter mode
 * keeps its description column at any width, which left the code about 200px wide at 1440px.
 * The peek is non-modal and clicks in the diff keep it open, so cmd-clicking another import swaps
 * the file.
 */
export function PrV2RepoFilePanel(props) {
  const $ = _c(23);
  let t0;
  if ($[0] !== props.number || $[1] !== props.owner || $[2] !== props.repo) {
    t0 = getPrV2ActiveFileStoreKey(props.owner, props.repo, props.number);
    $[0] = props.number;
    $[1] = props.owner;
    $[2] = props.repo;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const prKey = t0;
  let t1;
  if ($[4] !== prKey) {
    t1 = state => state.openFileByPr[prKey];
    $[4] = prKey;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const peek = usePrV2RepoFileStore(t1);
  const closeRepoFile = usePrV2RepoFileStore(_temp);
  let t2;
  if ($[6] !== closeRepoFile || $[7] !== prKey) {
    t2 = () => closeRepoFile(prKey);
    $[6] = closeRepoFile;
    $[7] = prKey;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const close = t2;
  if (!peek) {
    return null;
  }
  let t3;
  if ($[9] !== close) {
    t3 = open => {
      if (!open) {
        close();
      }
    };
    $[9] = close;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== peek.file.specifier) {
    t4 = <SheetTitle className="sr-only">{peek.file.specifier}</SheetTitle>;
    $[11] = peek.file.specifier;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== close || $[14] !== peek || $[15] !== props) {
    t5 = <PrV2RepoFileReader {...props} peek={peek} onClose={close} />;
    $[13] = close;
    $[14] = peek;
    $[15] = props;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t4 || $[18] !== t5) {
    t6 = <SheetContent side="right" data-testid="pr-v2-repo-file-panel" aria-describedby={undefined} onOpenAutoFocus={_temp2} onInteractOutside={_temp3} onEscapeKeyDown={_temp4} className="flex w-[min(44rem,100vw)] max-w-none flex-col gap-0 p-0 sm:max-w-none [&>button]:hidden">{t4}{t5}</SheetContent>;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t3 || $[21] !== t6) {
    t7 = <Sheet open={true} modal={false} onOpenChange={t3}>{t6}</Sheet>;
    $[20] = t3;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  return t7;
}
function _temp4(event_1) {
  if (isPrV2HotkeyTextEntryTarget(event_1.target)) {
    event_1.preventDefault();
  }
}
function _temp3(event_0) {
  return event_0.preventDefault();
}
function _temp2(event) {
  return event.preventDefault();
}
function _temp(state_0) {
  return state_0.closeRepoFile;
}
function PrV2RepoFileReader(t0) {
  const $ = _c(27);
  const {
    owner,
    repo,
    number,
    headSha,
    baseSha,
    display,
    peek,
    onClose
  } = t0;
  let t1;
  if ($[0] !== baseSha || $[1] !== headSha) {
    t1 = {
      headSha,
      baseSha
    };
    $[0] = baseSha;
    $[1] = headSha;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const query = usePrV2RepoFile(owner, repo, number, peek.file, t1);
  const result = query.data;
  let t2;
  if ($[3] !== display.disableLineNumbers || $[4] !== display.overflow || $[5] !== display.theme || $[6] !== display.themeType) {
    t2 = {
      theme: display.theme,
      themeType: display.themeType,
      overflow: display.overflow,
      disableLineNumbers: display.disableLineNumbers,
      disableFileHeader: true
    };
    $[3] = display.disableLineNumbers;
    $[4] = display.overflow;
    $[5] = display.theme;
    $[6] = display.themeType;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const options = t2;
  const title = result && result.status !== "not-found" ? result.path : peek.file.specifier;
  let t3;
  if ($[8] !== title) {
    t3 = <span className="min-w-0 flex-1 truncate font-mono text-xs" title={title}>{title}</span>;
    $[8] = title;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== result) {
    t4 = result ? <span className="shrink-0 font-mono text-xs text-muted-foreground" title={result.commitSha}>{result.commitSha.slice(0, 7)}</span> : null;
    $[10] = result;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <X className="size-4" />;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== onClose) {
    t6 = <Button variant="ghost" size="icon" className="size-7" aria-label="Close file" onClick={onClose}>{t5}</Button>;
    $[13] = onClose;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t3 || $[16] !== t4 || $[17] !== t6) {
    t7 = <header className="flex items-center gap-2 border-b border-border py-1.5 pr-1.5 pl-3">{t3}{t4}{t6}</header>;
    $[15] = t3;
    $[16] = t4;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== options || $[20] !== peek || $[21] !== query || $[22] !== result) {
    t8 = <div className="min-h-0 flex-1 overflow-auto">{result ? <PrV2RepoFileBody result={result} peek={peek} options={options} /> : query.isError && !query.isFetching ? <PrV2RepoFileMessage><span className="block">This file could not be loaded.</span><Button variant="outline" size="sm" onClick={() => void query.refetch()}>Retry</Button></PrV2RepoFileMessage> : <div role="status" aria-label="Loading file" className="space-y-2 p-3"><Skeleton className="h-3 w-2/3" /><Skeleton className="h-3 w-1/2" /><Skeleton className="h-3 w-3/4" /></div>}</div>;
    $[19] = options;
    $[20] = peek;
    $[21] = query;
    $[22] = result;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t7 || $[25] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
}
function PrV2RepoFileBody(t0) {
  const $ = _c(15);
  const {
    result,
    peek,
    options
  } = t0;
  switch (result.status) {
    case "found":
      {
        const t1 = `${result.commitSha}:${result.path}`;
        let t2;
        if ($[0] !== options || $[1] !== peek || $[2] !== result.contents || $[3] !== result.path || $[4] !== t1) {
          t2 = <PrV2RepoFileCode key={t1} path={result.path} contents={result.contents} peek={peek} options={options} />;
          $[0] = options;
          $[1] = peek;
          $[2] = result.contents;
          $[3] = result.path;
          $[4] = t1;
          $[5] = t2;
        } else {
          t2 = $[5];
        }
        return t2;
      }
    case "not-found":
      {
        let t1;
        if ($[6] !== peek.file.specifier || $[7] !== result.reason || $[8] !== result.tried) {
          t1 = <PrV2RepoFileNotFound reason={result.reason} tried={result.tried} specifier={peek.file.specifier} />;
          $[6] = peek.file.specifier;
          $[7] = result.reason;
          $[8] = result.tried;
          $[9] = t1;
        } else {
          t1 = $[9];
        }
        return t1;
      }
    case "too-large":
      {
        let t1;
        if ($[10] !== result.byteLength) {
          t1 = formatFileSize(result.byteLength);
          $[10] = result.byteLength;
          $[11] = t1;
        } else {
          t1 = $[11];
        }
        const t2 = `This file is ${t1}, too large to show here.`;
        let t3;
        if ($[12] !== t2) {
          t3 = <PrV2RepoFileMessage>{t2}</PrV2RepoFileMessage>;
          $[12] = t2;
          $[13] = t3;
        } else {
          t3 = $[13];
        }
        return t3;
      }
    case "binary":
      {
        let t1;
        if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <PrV2RepoFileMessage>This file is not text, so it can't be shown here.</PrV2RepoFileMessage>;
          $[14] = t1;
        } else {
          t1 = $[14];
        }
        return t1;
      }
  }
}

/**
 * The file's code, with the peek's line selected and scrolled to the middle of the panel. A peek
 * without a line opens the file at its top.
 */
function PrV2RepoFileCode(t0) {
  const $ = _c(18);
  const {
    path,
    contents,
    peek,
    options
  } = t0;
  const wrapperRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      container: null,
      line: null
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const scrollRef = useRef(t1);
  let t2;
  if ($[1] !== contents || $[2] !== path) {
    t2 = createPierreFileContents({
      name: path,
      contents,
      role: "new"
    });
    $[1] = contents;
    $[2] = path;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const file = t2;
  let t3;
  if ($[4] !== peek.line) {
    t3 = peek.line === null ? null : {
      start: peek.line,
      end: peek.line
    };
    $[4] = peek.line;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const selectedLines = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (container, _file, phase) => {
      scrollRef.current.container = phase === "unmount" ? null : container;
      flushPendingScroll(scrollRef.current);
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== options) {
    t5 = {
      ...options,
      onPostRender: t4
    };
    $[7] = options;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const fileOptions = t5;
  let t6;
  if ($[9] !== peek.line) {
    t6 = () => {
      scrollRef.current.line = peek.line;
      if (peek.line === null) {
        wrapperRef.current?.scrollIntoView({
          block: "start"
        });
      }
      flushPendingScroll(scrollRef.current);
    };
    $[9] = peek.line;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== file || $[12] !== peek) {
    t7 = [file, peek];
    $[11] = file;
    $[12] = peek;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  if ($[14] !== file || $[15] !== fileOptions || $[16] !== selectedLines) {
    t8 = <div ref={wrapperRef}><CodeFile className="block w-full" file={file} options={fileOptions} selectedLines={selectedLines} style={PIERRE_DIFF_PALETTE_OVERRIDES} /></div>;
    $[14] = file;
    $[15] = fileOptions;
    $[16] = selectedLines;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}

/** Centers the pending line in the panel once its row is on the page. */
function flushPendingScroll(scroll: PendingScroll): void {
  const row = scroll.line === null ? null : scroll.container?.shadowRoot?.querySelector(`[data-line="${scroll.line}"]`);
  if (row) {
    row.scrollIntoView({
      block: 'center'
    });
    scroll.line = null;
  }
}
function PrV2RepoFileNotFound(t0) {
  const $ = _c(9);
  const {
    reason,
    tried,
    specifier
  } = t0;
  switch (reason) {
    case "tree-truncated":
      {
        const t1 = `This repository is too large to search for ${specifier}.`;
        let t2;
        if ($[0] !== t1) {
          t2 = <PrV2RepoFileMessage>{t1}</PrV2RepoFileMessage>;
          $[0] = t1;
          $[1] = t2;
        } else {
          t2 = $[1];
        }
        return t2;
      }
    case "no-match":
      {
        const t1 = `Nothing in the repository matches ${specifier}.`;
        let t2;
        if ($[2] !== t1) {
          t2 = <span className="block">{t1}</span>;
          $[2] = t1;
          $[3] = t2;
        } else {
          t2 = $[3];
        }
        let t3;
        if ($[4] !== tried) {
          t3 = tried.length > 0 ? <details><summary className="cursor-pointer select-none">{`Looked for ${tried.length} ${tried.length === 1 ? "path" : "paths"}`}</summary><ul className="mt-1.5 space-y-0.5 font-mono text-xs">{tried.map(_temp5)}</ul></details> : null;
          $[4] = tried;
          $[5] = t3;
        } else {
          t3 = $[5];
        }
        let t4;
        if ($[6] !== t2 || $[7] !== t3) {
          t4 = <PrV2RepoFileMessage>{t2}{t3}</PrV2RepoFileMessage>;
          $[6] = t2;
          $[7] = t3;
          $[8] = t4;
        } else {
          t4 = $[8];
        }
        return t4;
      }
  }
}
function _temp5(path) {
  return <li key={path} className="break-all">{path}</li>;
}
function PrV2RepoFileMessage(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="space-y-2 p-3 text-sm text-muted-foreground">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function formatFileSize(byteLength: number): string {
  const kilobytes = byteLength / 1024;
  return kilobytes < 1024 ? `${FILE_SIZE_FORMAT.format(kilobytes)} KB` : `${FILE_SIZE_FORMAT.format(kilobytes / 1024)} MB`;
}